import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import useMasterMaps, { inputCls } from "../hooks/useMasterMaps";
import { BUSINESS_DRIVERS } from "../lib/constants";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function ScrNew() {
  const maps = useMasterMaps();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    branch_id: "", dept_id: "", desig_id: "", proposed_count: "",
    justification: "", business_driver: "", monthly_cost_impact: "", effective_from: "",
  });
  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (form.branch_id && form.dept_id && form.desig_id) {
      api.get("/scr/context", { params: { branch_id: form.branch_id, dept_id: form.dept_id, desig_id: form.desig_id } })
        .then(({ data }) => setCtx(data)).catch(() => setCtx(null));
    } else setCtx(null);
  }, [form.branch_id, form.dept_id, form.desig_id]);

  const delta = ctx != null && form.proposed_count !== "" ? Number(form.proposed_count) - ctx.current_count : null;
  const desigOptions = form.dept_id ? maps.designations.filter((d) => d.dept_id === form.dept_id) : maps.designations;

  const save = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post("/scr", { ...form, action });
      toast.success(`${data.scr_no} ${action === "submit" ? "submitted for approval" : "saved as draft"}`);
      navigate("/scr");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const label = (t, req) => (
    <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">
      {t}{req && <span className="text-[#BB162B]"> *</span>}
    </label>
  );

  return (
    <div data-testid="scr-new-page" className="max-w-2xl">
      <button onClick={() => navigate("/scr")} data-testid="scr-back-button"
        className="flex items-center gap-1.5 text-xs text-[#1E5AA8] hover:underline mb-4">
        <ArrowLeft size={13} /> Back to register
      </button>
      <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight mb-1">Raise Sanction Change Request</h1>
      <p className="text-sm text-[#6B7280] mb-6">Approval chain: Branch Head → VP Vertical → HR Head → AMD</p>

      <div className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            {label("Branch", true)}
            <select data-testid="scr-branch-select" className={inputCls} value={form.branch_id}
              onChange={(e) => set("branch_id", e.target.value)}>
              <option value="">— Select —</option>
              {maps.branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
            </select>
          </div>
          <div>
            {label("Department", true)}
            <select data-testid="scr-dept-select" className={inputCls} value={form.dept_id}
              onChange={(e) => { set("dept_id", e.target.value); set("desig_id", ""); }}>
              <option value="">— Select —</option>
              {maps.departments.map((d) => <option key={d.dept_id} value={d.dept_id}>{d.dept_name}</option>)}
            </select>
          </div>
          <div>
            {label("Designation", true)}
            <select data-testid="scr-desig-select" className={inputCls} value={form.desig_id}
              onChange={(e) => set("desig_id", e.target.value)}>
              <option value="">— Select —</option>
              {desigOptions.map((d) => <option key={d.desig_id} value={d.desig_id}>{d.desig_name}</option>)}
            </select>
          </div>
        </div>

        {ctx && (
          <div data-testid="scr-context-panel" className="flex flex-wrap gap-6 border border-[#BF9B30]/40 bg-[#BF9B30]/5 rounded-sm p-4 text-sm tabular-nums">
            <div><div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Current sanction</div><div className="text-xl font-semibold text-[#0E2240]">{ctx.current_count}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Active (incl. notice)</div><div className="text-xl font-semibold text-[#1B7F3B]">{ctx.active}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Version</div><div className="text-xl font-semibold text-[#6B7280]">v{ctx.version_no}</div></div>
            {delta != null && (
              <div><div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Delta</div>
                <div className={`text-xl font-semibold ${delta > 0 ? "text-[#1B7F3B]" : delta < 0 ? "text-[#BB162B]" : "text-[#6B7280]"}`}>{delta > 0 ? "+" : ""}{delta}</div></div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            {label("Proposed count", true)}
            <input data-testid="scr-proposed-input" type="number" min="0" className={inputCls}
              value={form.proposed_count} onChange={(e) => set("proposed_count", e.target.value)} />
          </div>
          <div>
            {label("Business driver")}
            <select data-testid="scr-driver-select" className={inputCls} value={form.business_driver}
              onChange={(e) => set("business_driver", e.target.value)}>
              <option value="">— Select —</option>
              {BUSINESS_DRIVERS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            {label("Monthly cost impact (₹)")}
            <input data-testid="scr-cost-input" type="number" className={inputCls}
              value={form.monthly_cost_impact} onChange={(e) => set("monthly_cost_impact", e.target.value)} />
          </div>
        </div>

        <div>
          {label("Justification", true)}
          <textarea data-testid="scr-justification-input" rows={3} className={inputCls}
            value={form.justification} onChange={(e) => set("justification", e.target.value)}
            placeholder="Why is this change needed? Reference volumes, norms or ratios." />
        </div>

        <div className="sm:w-1/3">
          {label("Effective from")}
          <input data-testid="scr-effective-input" type="date" className={inputCls}
            value={form.effective_from} onChange={(e) => set("effective_from", e.target.value)} />
        </div>

        <div className="flex gap-3 pt-2">
          <button data-testid="scr-save-draft-button" disabled={busy} onClick={() => save("draft")}
            className="px-5 py-2.5 text-sm border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50">
            Save Draft
          </button>
          <button data-testid="scr-submit-button" disabled={busy} onClick={() => save("submit")}
            className="px-5 py-2.5 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-50">
            {busy ? "Submitting…" : "Submit for Approval"}
          </button>
        </div>
      </div>
    </div>
  );
}
